import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Users2,
  ClipboardList,
  Settings as SettingsIcon,
  Download,
  LogOut,
  Sparkles,
} from "lucide-react";

const NAV = [
  { to: "/",          label: "Dashboard",     icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/leads",     label: "Leads",         icon: Users2,          testId: "nav-leads" },
  { to: "/standup",   label: "Daily Standup", icon: ClipboardList,   testId: "nav-standup" },
  { to: "/export",    label: "Weekly Export", icon: Download,        testId: "nav-export" },
  { to: "/settings",  label: "Settings",      icon: SettingsIcon,    testId: "nav-settings", roles: ["admin", "manager"] },
];

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const canSee = (item) => !item.roles || item.roles.includes(user?.role);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-slate-50 font-sans">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-slate-300 flex-shrink-0 flex flex-col border-r border-slate-800" data-testid="sidebar">
        <div className="px-6 py-6 flex items-center gap-2.5 border-b border-slate-800/70">
          <div className="w-8 h-8 rounded-md bg-amber-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading text-white font-bold tracking-tight text-[15px]">Shopmerse</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 -mt-0.5">CRM</div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.filter(canSee).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "nav-link-active"
                      : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800/70">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-700/30 border border-amber-600/40 text-amber-200 flex items-center justify-center text-xs font-semibold">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-100 truncate">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{user?.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); nav("/login"); }}
              className="text-slate-500 hover:text-slate-100 p-2 rounded-md hover:bg-slate-800/60 transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto bg-slate-50 flex flex-col relative">
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-6 sm:px-8 sticky top-0 z-20 shadow-sm">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight" data-testid="page-title">{title}</h1>
            {subtitle && <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">{actions}</div>
        </header>

        <div className="p-6 sm:p-8 space-y-6 max-w-[1600px] mx-auto w-full page-fade">
          {children}
        </div>
      </main>
    </div>
  );
}
